import { asDefined, mod, panic, UUID } from "@opendaw/lib-std";
import { EventCollection, TimeBase } from "@opendaw/lib-dsp";
import { AudioRegionBoxAdapter, RegionEditing, UnionAdapterTypes } from "@opendaw/studio-adapters";
import { RegionModifyStrategies } from "./RegionModifyStrategies";
import { TrackResolver } from "./TrackResolver";
// AudioRegions in absolute time-domain are allowed to overlap. Their duration changes when the tempo changes,
// but we do not truncate them to keep the original durations.
const allowOverlap = (region) => region instanceof AudioRegionBoxAdapter && region.timeBase !== TimeBase.Musical;
// A region's duration is stored as float32, and seconds-based audio regions carry double-precision
// ppqn drift (their ppqn is derived from seconds via the tempo map, e.g. complete = mask.complete + 2e-12).
// So a clip whose remainder falls below ~one float32 ulp truncates to duration 0, and later trips
// validateTrack / createTasksFromMasks (#1003). Treat a region within this tolerance of a mask boundary
// as touching it, so it is deleted rather than clipped to a zero-width sliver. The tolerance tracks
// float32 precision at the boundary magnitude (so it scales with project length) and is musically nil.
const Float32RelativeEpsilon = 2 ** -23; // float32 has 23 mantissa bits
const boundaryTolerance = (value) => Math.abs(value) * Float32RelativeEpsilon + 1e-3;
export class RegionClipResolver {
    static fromSelection(tracks, adapters, strategy, deltaIndex = 0) {
        const clipResolvers = new Map(tracks.map(track => ([track.listIndex, new RegionClipResolver(strategy, track)])));
        adapters.forEach(adapter => {
            const index = adapter.trackBoxAdapter.unwrap("trackBoxAdapter").listIndex + deltaIndex;
            asDefined(clipResolvers.get(index), `Cannot find clip resolver for index(${index})`)
                .addMask(adapter);
        });
        const tasks = Array.from(clipResolvers.values()).flatMap(resolver => resolver.#createSolver());
        return {
            postProcess: () => tasks.forEach(task => task()),
            trackResolver: TrackResolver.Identity
        };
    }
    static fromRange(track, position, complete) {
        // IdentityIncludeOrigin will include selected regions
        const clipResolver = new RegionClipResolver(RegionModifyStrategies.IdentityIncludeOrigin, track);
        clipResolver.addMaskRange(position, complete);
        return clipResolver.#createSolver();
    }
    static validateTracks(tracks) {
        for (const track of tracks) {
            this.validateTrack(track);
        }
    }
    // Fatal (throwing) by default so tests and dev builds fail loudly on a resolver invariant break; the shipped
    // studio app flips this off at boot (see boot.ts) so a stray overlap — or a project SAVED with a pre-existing
    // one — logs and continues instead of crashing the whole session.
    static fatal = true;
    static validateTrack(track) {
        const array = track.regions.collection.asArray();
        if (array.length === 0) {
            return;
        }
        let prev = array[0];
        if (prev.duration <= 0) {
            RegionClipResolver.#violation(`duration(${prev.duration}) must be positive`);
        }
        for (let i = 1; i < array.length; i++) {
            const next = array[i];
            if (next.duration <= 0) {
                RegionClipResolver.#violation(`duration(${next.duration}) must be positive`);
            }
            const overlaps = !allowOverlap(prev) && prev.complete > next.position + boundaryTolerance(next.position);
            if (overlaps) {
                RegionClipResolver.#reportOverlap(track, array, i);
                RegionClipResolver.#violation(`regions overlap: prev.complete(${prev.complete}) > next.position(${next.position})`);
            }
            prev = next;
        }
    }
    static #violation(message) {
        if (RegionClipResolver.fatal) {
            return panic(message);
        }
        console.warn(`[RegionClipResolver] ${message}`);
    }
    // Diagnostic only (#1054 family): the overlap panic below is unreproducible from a single geometry, so on
    // the failure path dump the full track layout into the shipped log buffer to reveal the accumulated
    // arrangement that produced the overlap. Fires once, immediately before the panic; no behaviour change.
    static #reportOverlap(track, regions, index) {
        const dump = (region, i) => ({
            i,
            uuid: UUID.toString(region.uuid),
            type: region.isAudioRegion() ? "audio" : region.isNoteRegion() ? "note" : "value",
            timeBase: region.isAudioRegion() ? region.box.timeBase.getValue() : undefined,
            position: region.position,
            duration: region.duration,
            complete: region.complete,
            loopOffset: region.loopOffset,
            loopDuration: region.loopDuration,
            mute: region.mute,
            mirrored: region.isMirrowed
        });
        console.debug("regions-overlap", {
            track: UUID.toString(track.uuid),
            trackType: track.type,
            count: regions.length,
            prev: dump(regions[index - 1], index - 1),
            next: dump(regions[index], index),
            regions: regions.map(dump)
        });
    }
    static createTasksFromMasks(regionIterator, maxComplete, masks, showOrigin) {
        const tasks = [];
        for (const region of regionIterator) {
            if (region.position >= maxComplete) {
                break;
            }
            if (region.isSelected && !showOrigin) {
                continue;
            }
            if (region.duration <= 0) {
                return panic(`Invalid duration(${region.duration})`);
            }
            const overlapping = masks.filter(mask => region.position < mask.complete && region.complete > mask.position);
            if (overlapping.length === 0) {
                continue;
            }
            for (let i = overlapping.length - 1; i >= 0; i--) {
                const { position, complete } = overlapping[i];
                const positionIn = region.position >= position - boundaryTolerance(position);
                const completeIn = region.complete <= complete + boundaryTolerance(complete);
                if (positionIn && completeIn) {
                    tasks.push({ type: "delete", region });
                    break;
                }
                else if (!positionIn && !completeIn) {
                    tasks.push({ type: "separate", region, begin: position, end: complete });
                }
                else if (completeIn) {
                    tasks.push({ type: "complete", region, position });
                }
                else {
                    tasks.push({ type: "start", region, position: complete });
                }
            }
        }
        return tasks;
    }
    static sortAndJoinMasks(masks) {
        if (masks.length === 0) {
            return panic("No clip-masks to solve");
        }
        if (masks.length === 1) {
            return [masks[0]];
        }
        // Sort by position (start time) - create a copy to avoid mutating input
        const sorted = [...masks].sort(EventCollection.DefaultComparator);
        const merged = [];
        let current = sorted[0];
        for (let i = 1; i < sorted.length; i++) {
            const next = sorted[i];
            // Check if the next mask overlaps or is adjacent to the current. Use the float32 boundary
            // tolerance (see #1003): seconds-based masks carry ppqn drift, so two masks that should abut
            // can sit a sub-ulp gap apart. Merging within tolerance prevents carving the ground region
            // into a zero-width sliver between them (#287).
            if (next.position <= current.complete + boundaryTolerance(current.complete)) {
                // Merge: extend current to cover both ranges
                current = {
                    type: "range",
                    position: current.position,
                    complete: Math.max(current.complete, next.complete)
                };
            }
            else {
                // No overlap or adjacency: save current and move to next
                merged.push(current);
                current = next;
            }
        }
        merged.push(current);
        return merged;
    }
    #strategy;
    #ground;
    #masks;
    constructor(strategy, ground) {
        this.#strategy = strategy;
        this.#ground = ground;
        this.#masks = [];
    }
    addMask(region) {
        const strategy = this.#strategy.selectedModifyStrategy();
        this.addMaskRange(strategy.readPosition(region), strategy.readComplete(region));
    }
    addMaskRange(position, complete) {
        this.#masks.push({ type: "range", position, complete });
    }
    #createSolver() {
        const masks = RegionClipResolver.sortAndJoinMasks(this.#masks);
        const maxComplete = masks.reduce((max, mask) => Math.max(max, mask.complete), 0);
        const tasks = RegionClipResolver.createTasksFromMasks(this.#ground.regions.collection.iterateRange(0, maxComplete), maxComplete, masks, this.#strategy.showOrigin());
        this.#masks.length = 0;
        return () => this.#executeTasks(tasks);
    }
    #executeTasks(tasks) {
        const sorted = tasks.toSorted(({ type: a }, { type: b }) => {
            if (a === "delete" && b !== "delete") {
                return 1;
            }
            if (b === "delete" && a !== "delete") {
                return -1;
            }
            return 0;
        });
        sorted.forEach(task => {
            const { type, region } = task;
            switch (type) {
                case "delete":
                    region.box.delete();
                    break;
                case "start":
                    // Round the new start UP to an integer. `position` is Int32, so a fractional mask
                    // boundary (seconds-based region) would truncate on store and desync from the Float32
                    // loop fields, leaving the region overlapping the clip by the dropped fraction (#287).
                    this.#trimStart(region, Math.ceil(task.position));
                    break;
                case "complete":
                    // Round the new end DOWN to an integer, for the same reason.
                    this.#trimComplete(region, Math.floor(task.position));
                    break;
                case "separate": {
                    const begin = Math.floor(task.begin);
                    const end = Math.ceil(task.end);
                    const leftEmpty = begin <= region.position;
                    const rightEmpty = end >= region.complete;
                    if (leftEmpty && rightEmpty) {
                        region.box.delete();
                    }
                    else if (leftEmpty) {
                        this.#trimStart(region, end);
                    }
                    else if (rightEmpty) {
                        this.#trimComplete(region, begin);
                    }
                    else {
                        RegionEditing.clip(region, begin, end);
                    }
                    break;
                }
            }
        });
    }
    #trimStart(region, position) {
        if (!UnionAdapterTypes.isLoopableRegion(region)) {
            return panic("Not yet implemented");
        }
        if (position >= region.complete) {
            return region.box.delete();
        }
        const delta = position - region.position;
        const oldDuration = region.duration;
        const oldLoopOffset = region.loopOffset;
        const oldLoopDuration = region.loopDuration;
        region.position = position;
        region.duration = oldDuration - delta;
        region.loopOffset = mod(oldLoopOffset + delta, oldLoopDuration);
    }
    #trimComplete(region, complete) {
        if (!UnionAdapterTypes.isLoopableRegion(region)) {
            return panic("Not yet implemented");
        }
        if (complete <= region.position) {
            return region.box.delete();
        }
        region.duration = complete - region.position;
    }
}
